import { localBackend } from "@/integrations/local/client";
import { services as staticServices, cases as staticCases, news as staticNews, team as staticTeam, employees as staticEmployees } from "@/data/site";

export interface DbService {
  id: string;
  slug: string;
  title: string;
  short: string;
  icon: string;
  sort_order: number;
}

export interface DbCaseStudy {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image_key: string;
  sort_order: number;
}

export interface DbNewsTeaser {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image_key: string;
  sort_order: number;
}

export interface DbTestimonial {
  id: string;
  quote: string;
  name: string;
  company: string;
  sort_order: number;
}

export interface DbTeamMember {
  id: string;
  name: string;
  role: string;
  featured_role: string | null;
  image_key: string | null;
  image_url: string | null;
  email: string | null;
  phone: string | null;
  office: string | null;
  is_featured: boolean;
  is_visible: boolean;
  sort_order: number;
}

export async function getServices(): Promise<DbService[]> {
  const { data } = await localBackend.from("services").select("*").order("sort_order", { ascending: true });
  if (data && (data as DbService[]).length > 0) return data as DbService[];
  return staticServices.map((s, i) => ({ id: String(i), slug: s.slug, title: s.title, short: s.short, icon: s.icon, sort_order: i }));
}

export async function getCaseStudies(): Promise<DbCaseStudy[]> {
  const { data } = await localBackend.from("case_studies").select("*").order("sort_order", { ascending: true });
  if (data && (data as DbCaseStudy[]).length > 0) return data as DbCaseStudy[];
  return staticCases.map((c, i) => ({ id: String(i), slug: c.slug, title: c.title, excerpt: c.excerpt, image_key: c.image, sort_order: i }));
}

export async function getNewsTeasers(): Promise<DbNewsTeaser[]> {
  const { data } = await localBackend.from("news_teasers").select("*").order("sort_order", { ascending: true });
  if (data && (data as DbNewsTeaser[]).length > 0) return data as DbNewsTeaser[];
  return staticNews.map((n, i) => ({ id: String(i), slug: n.slug, title: n.title, excerpt: n.excerpt, date: n.date, category: n.category, image_key: n.slug, sort_order: i }));
}

export async function getTestimonials(): Promise<DbTestimonial[]> {
  const { data } = await localBackend.from("testimonials").select("*").order("sort_order", { ascending: true });
  if (data && (data as DbTestimonial[]).length > 0) return data as DbTestimonial[];
  return [
    { id: "0", quote: "Samuel kunde snabbt sätta sig in i verksamheten och hans arbete var avgörande för våra bokslut. Vi sätter stort värde på hans kunskaper och rådgivning.", name: "Stefan Johansson", company: "Kaj Johansson Åkeri AB", sort_order: 0 },
    { id: "1", quote: "Med Cloud fick vi äntligen en byrå som ringer oss innan vi hinner undra. Det är en helt annan trygghet i vardagen.", name: "Alexander Elebro", company: "PT Online", sort_order: 1 },
    { id: "2", quote: "Den digitala omställningen tillsammans med Cloud gav oss bättre kontroll på siffrorna — och mer tid för gästerna.", name: "Linnea Sundqvist", company: "Caféet Paus, Vännäs", sort_order: 2 },
  ];
}

export async function getTeamMembers(): Promise<DbTeamMember[]> {
  const { data } = await localBackend.from("team_members").select("*").order("sort_order", { ascending: true });
  if (data && (data as DbTeamMember[]).length > 0) {
    // Rows written before is_visible existed don't have the field - treat as visible.
    return (data as DbTeamMember[]).map((m) => ({ ...m, is_visible: m.is_visible ?? true }));
  }
  return staticEmployees.map((e, i) => ({
    id: String(i),
    name: e.name,
    role: e.role,
    featured_role: null,
    image_key: ("image" in e ? e.image : null) ?? null,
    image_url: null,
    email: ("email" in e ? e.email : null) ?? null,
    phone: ("phone" in e ? e.phone : null) ?? null,
    office: ("office" in e ? e.office : null) ?? null,
    is_featured: staticTeam.some((t) => t.name === e.name),
    is_visible: true,
    sort_order: i,
  }));
}

// Public-facing pages should use this - it respects the admin's visibility toggle.
// getTeamMembers() itself stays unfiltered so the admin panel can see/edit hidden members.
export async function getVisibleTeamMembers(): Promise<DbTeamMember[]> {
  const all = await getTeamMembers();
  return all.filter((m) => m.is_visible !== false);
}

export async function getFeaturedTeamMembers(): Promise<DbTeamMember[]> {
  const all = await getVisibleTeamMembers();
  return all.filter((m) => m.is_featured).sort((a, b) => a.sort_order - b.sort_order);
}
